<dialog
  #dialog
  aria-labelledby="club-profile-title"
  (cancel)="$event.preventDefault(); dismissible() && close()"
  (click)="backdrop($event)"
  class="bg-white text-slate-900 dark:bg-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl flex flex-col"
>
  <div class="p-4 sm:p-5 flex flex-col h-full max-h-[100dvh]">
    <!-- Header with Close Button -->
    <div class="flex items-start justify-between gap-4 pb-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
      <div class="flex items-center gap-3.5 min-w-0">
        <div class="w-10 h-12 flex items-center justify-center shrink-0">
          <app-club-shield [club]="club() || { id: clubId(), name: displayName(), code: displayCode() }" size="md" />
        </div>
        <div class="min-w-0">
          <div class="flex items-center gap-2.5 flex-wrap">
            <h2 id="club-profile-title" class="text-lg font-bold text-slate-900 dark:text-white leading-tight break-words">
              {{ displayName() }}
            </h2>            
          </div>
          <div class="flex items-center gap-2 mt-1 text-xs text-slate-500 dark:text-slate-400 flex-wrap">
            @if (!loading() && !error()) {
              @if (realPlayers().length > 0) {
                <span class="inline-flex items-center gap-1" title="Valor de la plantilla">
                  <app-icon name="coins" [size]="13" aria-hidden="true" />
                  <span class="sr-only">Valor de la plantilla:</span>
                  <strong class="text-slate-700 dark:text-slate-300 font-mono">{{ totalMarketValue().toFixed(1) }} M€</strong>
                </span>
                <span>•</span>
                <span class="inline-flex items-center gap-1" title="Suma de las medias globales de los jugadores de la plantilla">
                  <app-icon name="star" [size]="13" class="text-amber-500" aria-hidden="true" />
                  <span class="sr-only">Suma de medias globales:</span>
                  <strong class="text-amber-600 dark:text-amber-400 font-mono">{{ formatPoints(totalGlobalAverage()) }}</strong>
                </span>
                <span>•</span>
                <span class="inline-flex items-center gap-1" title="Suma de las medias de los últimos tres partidos mostradas en la plantilla">
                  <app-icon name="trending-up" [size]="13" class="text-emerald-500" aria-hidden="true" />
                  <span class="sr-only">Suma de medias de los últimos tres partidos:</span>
                  <strong class="text-emerald-600 dark:text-emerald-400 font-mono">{{ formatPoints(totalLast3Average()) }}</strong>
                </span>
              }
            }
          </div>
        </div>
      </div>
      <button
        type="button"
        aria-label="Cerrar ficha del club"
        class="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 shrink-0 cursor-pointer"
        (click)="close()"
      >
        ✕
      </button>
    </div>

    <!-- Body States -->
    @if (loading()) {
      <div class="py-16 text-center space-y-3 flex-1 flex flex-col items-center justify-center">
        <div class="w-8 h-8 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
        <p role="status" class="text-sm text-slate-500 dark:text-slate-400">Cargando ficha del club…</p>
      </div>
    } @else if (error()) {
      <div class="py-12 px-4 text-center space-y-4 flex-1 flex flex-col items-center justify-center">
        <div class="w-12 h-12 rounded-2xl bg-rose-50 dark:bg-rose-950/40 text-rose-500 flex items-center justify-center">
          <app-icon name="alert-circle" [size]="24" />
        </div>
        <p role="alert" class="text-sm text-rose-600 dark:text-rose-400 font-medium">{{ error() }}</p>
        <button
          type="button"
          (click)="retry()"
          class="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl cursor-pointer shadow-xs transition-colors inline-flex items-center gap-1.5"
        >
          <span>Reintentar</span>
        </button>
      </div>
    } @else {
      <!-- Squad Section -->
      <section class="mt-4 flex-1 flex flex-col min-h-0" aria-label="Plantilla de futbolistas">
        <div class="flex items-center justify-between pb-2 shrink-0">
          <h3 class="text-xs font-bold tracking-wide text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
            <span class="uppercase">Plantilla</span>
            <span class="text-[11px] font-normal tracking-normal text-slate-400 dark:text-slate-500">({{ realPlayers().length }} profesionales)</span>
          </h3>
          @if (!isUserClub() && clubId() !== game.userClub()?.id) {
            <span class="text-[11px] px-2.5 py-0.5 rounded-full font-semibold border"
              [ngClass]="game.hasMaxPortfolio() ? 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-800' : 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700'">
              Cartera: {{ game.portfolioCount() }}/3
            </span>
          }
        </div>

        @if (realPlayers().length === 0 && !coach()) {
          <div class="py-12 px-4 text-center rounded-xl border border-dashed border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30 flex-1 flex flex-col items-center justify-center">
            <div class="w-12 h-12 mb-3 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400">
              <app-icon name="users" [size]="22" />
            </div>
            <p class="text-sm font-medium text-slate-600 dark:text-slate-300">
              Este club todavía no tiene jugadores en su plantilla
            </p>
          </div>
        } @else {
          <div class="space-y-3 mt-1 flex-1 overflow-y-auto pr-1 min-h-0">
            <!-- Entrenador del Club (as the first card of the squad, matching image reference) -->
            @if (coach(); as c) {
              <div
                class="p-3.5 sm:p-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/90 shadow-2xs hover:shadow-md hover:border-indigo-300 dark:hover:border-indigo-700/60 transition-all flex flex-col justify-between space-y-2.5 text-left"
              >
                <!-- Card Header: Escudo + Nombre + Badge DT + Estado Activo -->
                <div class="flex items-start justify-between gap-3">
                  <div class="flex items-center space-x-2.5 min-w-0">
                    <div class="relative w-8 h-8 rounded-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center p-0.5 shrink-0 shadow-2xs">
                      @if (coachShield(c); as crestUrl) {
                        <img
                          [src]="crestUrl"
                          [alt]="c.realClub?.name || c.realClub?.shortName || 'Club'"
                          (error)="onCrestError($event)"
                          class="w-full h-full object-contain"
                          referrerpolicy="no-referrer"
                        />
                      } @else {
                        <div class="w-full h-full flex items-center justify-center text-slate-400">
                          <app-icon name="shield" [size]="16" />
                        </div>
                      }
                    </div>
                    <div class="min-w-0">
                      <h5 class="font-bold text-sm text-slate-900 dark:text-white truncate">
                        {{ c.name }}
                      </h5>
                      <div class="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        @if (getCoachSignedAt(c); as signedAt) {
                          <span class="truncate">Firmado: <strong>{{ signedAt | date:'dd/MM/yyyy HH:mm' }}</strong></span>
                        } @else if (c.age != null) {
                          <span>{{ c.age }} años</span>
                        } @else {
                          <span>Entrenador Principal</span>
                        }
                      </div>
                    </div>
                  </div>

                  <div class="shrink-0">
                    <span class="px-2 py-0.5 rounded text-[10px] font-bold bg-indigo-50 text-indigo-700 dark:bg-indigo-950/80 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800/80">
                      DT
                    </span>
                  </div>
                </div>

                <!-- 3 Metric Boxes: DEFENSA, CONTROL, ATAQUE (con idéntico tamaño y formato que los jugadores) -->
                <div class="grid grid-cols-3 gap-2 p-2.5 rounded-xl bg-slate-50 dark:bg-slate-950/70 border border-slate-100 dark:border-slate-800/80 text-xs" aria-label="Mejoras del entrenador">
                  <div>
                    <div class="text-[10px] font-medium text-slate-500 dark:text-slate-400 truncate">Defensa</div>
                    <div class="font-bold text-amber-600 dark:text-amber-400 font-mono text-sm mt-0.5 flex items-center space-x-1">
                      <app-icon name="shield" [size]="12" class="text-amber-500 shrink-0" />
                      <span>@if (c.defense != null) { +{{ c.defense | number:'1.0-2' }}% } @else { — }</span>
                    </div>
                  </div>

                  <div>
                    <div class="text-[10px] font-medium text-slate-500 dark:text-slate-400 truncate">Control</div>
                    <div class="font-bold text-blue-600 dark:text-blue-400 font-mono text-sm mt-0.5 flex items-center space-x-1">
                      <app-icon name="activity" [size]="12" class="text-blue-500 shrink-0" />
                      <span>@if (c.midfield != null) { +{{ c.midfield | number:'1.0-2' }}% } @else { — }</span>
                    </div>
                  </div>

                  <div>
                    <div class="text-[10px] font-medium text-slate-500 dark:text-slate-400 truncate">Ataque</div>
                    <div class="font-bold text-emerald-600 dark:text-emerald-400 font-mono text-sm mt-0.5 flex items-center space-x-1">
                      <app-icon name="zap" [size]="12" class="text-emerald-500 shrink-0" />
                      <span>@if (c.attack != null) { +{{ c.attack | number:'1.0-2' }}% } @else { — }</span>
                    </div>
                  </div>
                </div>
              </div>
            }

            @for (player of sortedRealPlayers(); track (player.id || player.playerDefinitionId || $index)) {
              <div
                class="p-3.5 sm:p-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/90 shadow-2xs hover:shadow-md hover:border-emerald-300 dark:hover:border-emerald-700/60 transition-all flex flex-col justify-between space-y-2.5 text-left"
              >
                <!-- Card Header: Escudo + Nombre + Posiciones + Estado -->
                <div class="flex items-start justify-between gap-3">
                  <div class="flex items-center space-x-2.5 min-w-0">
                    <!-- Escudo del Club Real -->
                    <div class="relative w-8 h-8 rounded-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center p-0.5 shrink-0 shadow-2xs">
                      @if (clubShield(player); as crestUrl) {
                        <img
                          [src]="crestUrl"
                          [alt]="player.realClub?.shortName || 'Club'"
                          (error)="onCrestError($event)"
                          referrerpolicy="no-referrer"
                          class="w-full h-full object-contain"
                          [title]="player.realClub?.name || 'Club'"
                        />
                      } @else {
                        <span class="text-[9px] font-mono font-bold text-slate-500 uppercase">
                          {{ player.realClub?.shortName || 'CLB' }}
                        </span>
                      }
                    </div>

                    <div class="min-w-0">
                      <div class="flex items-center gap-1.5 flex-wrap">
                        <button
                          type="button"
                          (click)="openPlayer(player)"
                          class="font-bold text-sm text-slate-900 dark:text-white hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors cursor-pointer text-left focus:outline-none"
                        >
                          {{ player.displayName }}
                        </button>
                        <span class="inline-flex items-center gap-1">
                          <span
                            class="px-1.5 py-0.2 rounded text-[10px] font-bold border"
                            [ngClass]="positionBadgeClass(player.position)"
                            [title]="fullPosition(player.position)"
                          >
                            {{ positionAbbr(player.position) }}
                          </span>
                          @for (sec of player.secondaryPositions; track $index) {
                            <span
                              class="px-1.5 py-0.2 rounded text-[10px] font-bold border"
                              [ngClass]="positionBadgeClass(sec)"
                              [title]="fullPosition(sec)"
                            >
                              {{ positionAbbr(sec) }}
                            </span>
                          }
                        </span>
                      </div>
                      <div class="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        <span>{{ player.age != null ? player.age + ' años' : '' }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Status / Availability Badge -->
                  <div class="shrink-0">
                    @if (hasUnavailabilities(player)) {
                      <span class="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-800 dark:bg-rose-950/70 dark:text-rose-300 border border-rose-300 dark:border-rose-800">
                        <span class="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0"></span>
                        <span>No disponible</span>
                      </span>
                    } @else {
                      <span class="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/70 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800">
                        <app-icon name="check" [size]="11" />
                        <span>Disponible</span>
                      </span>
                    }
                  </div>
                </div>

                <!-- Alerta de Indisponibilidad si existe -->
                @if (hasUnavailabilities(player)) {
                  <div
                    class="flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-medium border bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800/60"
                    [title]="getUnavailabilityLabel(player)"
                  >
                    <div class="flex items-center space-x-1.5 truncate">
                      <app-icon [name]="getUnavailabilityIcon(player.unavailabilities?.[0]?.type)" [size]="13" class="shrink-0" />
                      <span class="font-bold uppercase text-[10px] tracking-wide">{{ getUnavailabilityLabel(player) }}</span>
                    </div>
                    @if (player.unavailabilities?.[0]?.expectedEndDate; as end) {
                      <span class="text-[10px] font-normal opacity-80 shrink-0 ml-1.5">hasta {{ end }}</span>
                    }
                  </div>
                }

                <!-- 3 Metric Boxes: Valor de Mercado, Media Global, Media 3 Part. -->
                <div class="grid grid-cols-3 gap-2 p-2.5 rounded-xl bg-slate-50 dark:bg-slate-950/70 border border-slate-100 dark:border-slate-800/80 text-xs">
                  <div>
                    <div class="text-[10px] font-medium text-slate-500 dark:text-slate-400 truncate">Valor Mercado</div>
                    <div class="font-bold text-slate-900 dark:text-white font-mono text-sm mt-0.5">
                      {{ getPlayerMarketValue(player) }}
                    </div>
                  </div>

                  <div>
                    <div class="text-[10px] font-medium text-slate-500 dark:text-slate-400 truncate">Media Global</div>
                    <div class="font-bold text-amber-600 dark:text-amber-400 font-mono text-sm mt-0.5 flex items-center space-x-1">
                      <app-icon name="star" [size]="12" class="text-amber-500" />
                      <span>{{ formatPoints(player.averagePoints ?? player.rating) }}</span>
                    </div>
                  </div>

                  <div>
                    <div class="text-[10px] font-medium text-slate-500 dark:text-slate-400 truncate">Media 3 Part.</div>
                    <div class="font-bold text-emerald-600 dark:text-emerald-400 font-mono text-sm mt-0.5 flex items-center space-x-1">
                      <app-icon name="trending-up" [size]="12" class="text-emerald-500" />
                      <span>{{ formatPoints(player.last3AveragePoints ?? player.averagePoints ?? player.rating) }}</span>
                    </div>
                  </div>
                </div>

                <!-- Acciones del Futbolista (Cartera para clubes rivales) -->
                @if (!isUserClub() && clubId() !== game.userClub()?.id) {
                  <div class="pt-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-2">
                    <button
                      type="button"
                      (click)="openPlayer(player)"
                      class="px-2.5 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-[11px] font-semibold text-slate-600 dark:text-slate-300 transition-colors inline-flex items-center gap-1 cursor-pointer"
                    >
                      <app-icon name="user" [size]="12" />
                      <span>Ficha</span>
                    </button>
                    <button
                      type="button"
                      (click)="togglePortfolio(player)"
                      [disabled]="!isInPortfolio(player) && game.hasMaxPortfolio()"
                      class="flex-1 py-1.5 px-3 rounded-xl font-semibold text-xs transition-all flex items-center justify-center space-x-1.5 shadow-xs"
                      [ngClass]="isInPortfolio(player)
                        ? 'bg-amber-100 text-amber-900 dark:bg-amber-950/70 dark:text-amber-300 border border-amber-300 dark:border-amber-800 cursor-pointer hover:bg-amber-200 dark:hover:bg-amber-900/80'
                        : (!isInPortfolio(player) && game.hasMaxPortfolio()
                            ? 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 border border-slate-200 dark:border-slate-700 cursor-not-allowed opacity-60'
                            : 'bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white cursor-pointer')"
                      [title]="isInPortfolio(player) ? 'En cartera (' + game.portfolioCount() + '/3) · Clic para retirar' : (game.hasMaxPortfolio() ? 'Cartera llena (3/3). Retira un jugador antes de añadir otro.' : 'Añadir a Cartera (' + game.portfolioCount() + '/3)')"
                    >
                      <app-icon [name]="isInPortfolio(player) ? 'bookmark-check' : (game.hasMaxPortfolio() ? 'lock' : 'bookmark')" [size]="13" />
                      <span>{{ isInPortfolio(player) ? 'En Cartera (' + game.portfolioCount() + '/3)' : (game.hasMaxPortfolio() ? 'Cartera llena (3/3)' : 'Añadir a Cartera (' + game.portfolioCount() + '/3)') }}</span>
                    </button>
                  </div>
                } @else {
                  <div class="pt-1 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-xs">
                    <button
                      type="button"
                      (click)="openPlayer(player)"
                      class="text-xs font-semibold text-slate-600 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors inline-flex items-center gap-1 cursor-pointer"
                    >
                      <app-icon name="user" [size]="12" />
                      <span>Ver ficha</span>
                    </button>
                    @if (player.realClub?.name; as clubName) {
                      <span class="text-[11px] text-slate-400 dark:text-slate-500 truncate">{{ clubName }}</span>
                    }
                  </div>
                }
              </div>
            }
          </div>
        }
      </section>
    }

    <!-- Footer -->
    <div class="mt-4 pt-3 flex items-center justify-end border-t border-slate-100 dark:border-slate-800 shrink-0">
      <button
        type="button"
        (click)="close()"
        class="px-4 py-2 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-xs font-bold rounded-xl cursor-pointer hover:bg-slate-800 dark:hover:bg-slate-200 transition-colors"
      >
        Cerrar
      </button>
    </div>
  </div>
</dialog>

<!-- Nested Player Profile Modal -->
@if (selectedPlayer(); as sp) {
  <app-player-profile
    [playerDefinitionId]="sp.id"
    [playerName]="sp.name"
    (closed)="closePlayer()"
  />
}
